import { Injectable, inject } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { collection, query, where, getDocs, getDoc, doc, writeBatch, DocumentData, QuerySnapshot, onSnapshot, addDoc, updateDoc, deleteField, setDoc } from 'firebase/firestore';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class StoreService {
  private firestore = inject(Firestore);

  getStores(): Observable<any[]> {
    const storesCol = collection(this.firestore, 'stores');
    return new Observable<any[]>(observer => {
      const unsubscribe = onSnapshot(storesCol, (snapshot) => {
        const stores = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        observer.next(stores);
      }, (error) => observer.error(error));
      return unsubscribe;
    });
  }

  async getStoresSnapshot(): Promise<QuerySnapshot<DocumentData>> {
    const storesCol = collection(this.firestore, 'stores');
    return getDocs(storesCol);
  }

  async getStoresByCompanyId(companyId: string): Promise<any[]> {
    const storesRef = collection(this.firestore, 'stores');
    // Handle both new array structure and legacy single field
    const qStoresArray = query(storesRef, where('companyIds', 'array-contains', companyId));
    const qStoresLegacy = query(storesRef, where('companyId', '==', companyId));
    
    const [snapArray, snapLegacy] = await Promise.all([getDocs(qStoresArray), getDocs(qStoresLegacy)]);
    
    const stores: any[] = [];
    const seenIds = new Set<string>();

    const addDocs = (snap: QuerySnapshot<DocumentData>) => {
      snap.forEach(d => {
        if (!seenIds.has(d.id)) {
          seenIds.add(d.id);
          stores.push(d.data());
        }
      });
    };

    addDocs(snapArray);
    addDocs(snapLegacy);
    
    return stores;
  }

  async createNewStores(stores: any[], userName: string): Promise<void> {
    let newStoresBatch = writeBatch(this.firestore);
    let newOps = 0;
    
    for (const store of stores) {
      const newStoreRef = doc(collection(this.firestore, 'stores'));
      newStoresBatch.set(newStoreRef, {
        storeId: store.storeId,
        companyIds: [],
        createdAt: new Date(),
        createdBy: userName
      });
      newOps++;
      
      if (newOps >= 490) {
        await newStoresBatch.commit();
        newStoresBatch = writeBatch(this.firestore);
        newOps = 0;
      }
    }
    if (newOps > 0) await newStoresBatch.commit();
  }

  async getDevices(storeDocId: string, customStoreId?: string): Promise<any[]> {
    const devicesCol = collection(this.firestore, 'devices');
    const queries = [
      query(devicesCol, where('storeId', '==', storeDocId)),
      query(devicesCol, where('storeDocId', '==', storeDocId))
    ];
    if (customStoreId) {
      queries.push(query(devicesCol, where('storeId', '==', customStoreId)));
    }

    const snapshots = await Promise.all(queries.map(q => getDocs(q)));
    const deviceMap = new Map<string, any>();
    snapshots.forEach(snap => {
      snap.docs.forEach(doc => {
        if (doc.id !== 'global' && !(doc.data() as any).isDeleted) {
          const data = doc.data();
          deviceMap.set(doc.id, {
            id: doc.id,
            deviceId: data['deviceId'] || doc.id,
            deviceName: data['deviceName'] || data['name'] || doc.id,
            ...data
          });
        }
      });
    });

    return Array.from(deviceMap.values());
  }

  async getAllDevices(): Promise<any[]> {
    const devicesCol = collection(this.firestore, 'devices');
    const snap = await getDocs(devicesCol);
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(d => !(d as any).isDeleted);
  }

  getAllDevicesLive(): Observable<any[]> {
    const devicesCol = collection(this.firestore, 'devices');
    return new Observable<any[]>(observer => {
      const unsubscribe = onSnapshot(devicesCol, (snapshot) => {
        const devices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        observer.next(devices.filter(d => !(d as any).isDeleted));
      }, (error) => observer.error(error));
      return unsubscribe;
    });
  }

  async getDevice(deviceId: string): Promise<any | null> {
    const deviceDocRef = doc(this.firestore, 'devices', deviceId);
    const snap = await getDoc(deviceDocRef);
    if (snap.exists()) {
      return { id: snap.id, ...snap.data() };
    }
    return null;
  }

  async addStore(store: any): Promise<void> {
    const storesCol = collection(this.firestore, 'stores');
    const { devices, ...storeData } = store;
    const deviceCount = (devices && Array.isArray(devices)) ? devices.length : 0;
    const friendlyName = (storeData.friendlyName || storeData.friendlyStoreName || '').trim();
    
    const docRef = await addDoc(storesCol, { 
      ...storeData, 
      friendlyName: friendlyName,
      friendlyStoreName: friendlyName,
      deviceCount: deviceCount,
      createdAt: new Date() 
    });

    if (devices && devices.length > 0) {
      const devicesCol = collection(this.firestore, 'devices');
      const batch = writeBatch(this.firestore);
      devices.forEach((device: any) => {
        const { isNew, id, ...cleanDevice } = device;
        const devId = cleanDevice.deviceId ? String(cleanDevice.deviceId) : id;
        if (devId) {
          const deviceRef = doc(devicesCol, devId);
          batch.set(deviceRef, {
            ...cleanDevice,
            deviceId: devId,
            storeId: storeData.storeId || docRef.id,
            storeDocId: docRef.id
          }, { merge: true });
        }
      });
      await batch.commit();
    }
  }

  async updateStore(id: string, store: any): Promise<void> {
    const docRef = doc(this.firestore, 'stores', id);
    const { devices, ...storeData } = store;
    
    if (storeData.friendlyName !== undefined || storeData.friendlyStoreName !== undefined) {
      const friendlyName = (storeData.friendlyName || storeData.friendlyStoreName || '').trim();
      storeData.friendlyName = friendlyName;
      storeData.friendlyStoreName = friendlyName;
    }
    if (devices !== undefined && Array.isArray(devices)) {
      storeData.deviceCount = devices.length;
    }

    const batch = writeBatch(this.firestore);
    batch.update(docRef, storeData);
    
    if (devices !== undefined) {
      const existingDevices = await this.getDevices(id, storeData.storeId);
      const currentDeviceIds = new Set(
        devices.map((d: any) => d.deviceId ? String(d.deviceId) : (d.id ? String(d.id) : null)).filter(Boolean)
      );
      
      const devicesCol = collection(this.firestore, 'devices');
      existingDevices.forEach(d => {
        if (!currentDeviceIds.has(d.id) && !currentDeviceIds.has(d.deviceId)) {
          const deviceRef = doc(devicesCol, d.id || d.deviceId);
          batch.update(deviceRef, {
            storeId: '',
            storeDocId: ''
          });
        }
      });
  
      devices.forEach((device: any) => {
        const { isNew, id: mappedId, ...cleanDevice } = device;
        const devId = cleanDevice.deviceId ? String(cleanDevice.deviceId) : mappedId;
        if (devId) {
          const deviceRef = doc(devicesCol, devId);
          batch.set(deviceRef, {
            ...cleanDevice,
            deviceId: devId,
            storeId: storeData.storeId || id,
            storeDocId: id
          }, { merge: true });
        }
      });
    }
    
    await batch.commit();
  }

  async updateDeviceStore(deviceId: string, storeId: string, storeDocId: string): Promise<void> {
    const deviceRef = doc(this.firestore, 'devices', deviceId);
    await updateDoc(deviceRef, { storeId: storeId, storeDocId: storeDocId });
    // Keep machine counts in sync across stores
    try {
      await this.syncMachineCounts();
    } catch (e) {
      console.warn('Failed to sync machine counts after device store update', e);
    }
  }

  /**
   * Scans all active devices and updates the deviceCount field on each store in Firestore.
   */
  async syncDeviceCounts(): Promise<{ updatedStores: number; totalStores: number }> {
    const [devices, storesSnap] = await Promise.all([
      this.getAllDevices(),
      this.getStoresSnapshot()
    ]);

    const storeMap = new Map<string, { docId: string; currentDeviceCount: number }>();
    const storeIdToDocId = new Map<string, string>();

    storesSnap.docs.forEach(d => {
      const data = d.data();
      const currentDeviceCount = typeof data['deviceCount'] === 'number' ? data['deviceCount'] : -1;
      storeMap.set(d.id, { docId: d.id, currentDeviceCount });
      if (data['storeId']) {
        storeIdToDocId.set(data['storeId'], d.id);
      }
    });

    const counts = new Map<string, number>();
    storesSnap.docs.forEach(d => counts.set(d.id, 0));

    devices.forEach(device => {
      if (device.id === 'global' || device.isDeleted) return;
      let targetDocId = device.storeDocId;
      if (!targetDocId && device.storeId) {
        targetDocId = storeIdToDocId.get(device.storeId) || (storeMap.has(device.storeId) ? device.storeId : null);
      }
      if (targetDocId && counts.has(targetDocId)) {
        counts.set(targetDocId, (counts.get(targetDocId) || 0) + 1);
      }
    });

    let updatedStores = 0;
    let batch = writeBatch(this.firestore);
    let batchCount = 0;

    for (const [docId, count] of counts.entries()) {
      const storeInfo = storeMap.get(docId);
      if (storeInfo && storeInfo.currentDeviceCount !== count) {
        const storeRef = doc(this.firestore, 'stores', docId);
        batch.update(storeRef, { 
          deviceCount: count
        });
        batchCount++;
        updatedStores++;

        if (batchCount >= 450) {
          await batch.commit();
          batch = writeBatch(this.firestore);
          batchCount = 0;
        }
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }

    return { updatedStores, totalStores: storesSnap.docs.length };
  }

  /**
   * Alias for backward compatibility.
   */
  syncMachineCounts(): Promise<{ updatedStores: number; totalStores: number }> {
    return this.syncDeviceCounts();
  }

  async updateDeviceTestingStatus(deviceId: string, isTesting: boolean): Promise<void> {
    const deviceRef = doc(this.firestore, 'devices', deviceId);
    await updateDoc(deviceRef, { isTesting: isTesting });
  }

  async updateDeviceName(deviceId: string, name: string): Promise<void> {
    const deviceRef = doc(this.firestore, 'devices', deviceId);
    await updateDoc(deviceRef, { name: name, deviceName: name });
  }

  async deleteDevice(deviceId: string): Promise<void> {
    const devRef = doc(this.firestore, 'devices', deviceId);
    await updateDoc(devRef, { isDeleted: true, updatedAt: new Date() });
    await this.syncDeviceCounts();
  }

  async deleteStore(id: string): Promise<void> {
    const docRef = doc(this.firestore, 'stores', id);
    await updateDoc(docRef, { isDeleted: true });
  }

  async deleteStores(ids: string[]): Promise<void> {
    const batch = writeBatch(this.firestore);
    ids.forEach(id => {
      const docRef = doc(this.firestore, 'stores', id);
      batch.update(docRef, { isDeleted: true });
    });
    await batch.commit();
  }

  /**
   * Deletes the legacy machineCount field from all documents in the stores collection.
   */
  async removeMachineCountField(): Promise<{ removedStores: number; totalStores: number }> {
    const storesCol = collection(this.firestore, 'stores');
    const snap = await getDocs(storesCol);
    let removedStores = 0;
    let batch = writeBatch(this.firestore);
    let batchCount = 0;

    for (const d of snap.docs) {
      const data = d.data();
      if (data && 'machineCount' in data) {
        batch.update(d.ref, { machineCount: deleteField() });
        batchCount++;
        removedStores++;

        if (batchCount >= 450) {
          await batch.commit();
          batch = writeBatch(this.firestore);
          batchCount = 0;
        }
      }
    }
    if (batchCount > 0) {
      await batch.commit();
    }
    return { removedStores, totalStores: snap.docs.length };
  }

  async createDevice(deviceData: {
    deviceId: string;
    deviceName: string;
    storeId: string;
    storeDocId?: string;
    brandId: string;
    useCash?: boolean;
    useCard?: boolean;
    isTesting?: boolean;
  }): Promise<void> {
    const trimmedId = deviceData.deviceId.trim();
    if (!trimmedId) throw new Error('Device ID is required.');

    const devDocRef = doc(this.firestore, 'devices', trimmedId);
    const existingSnap = await getDoc(devDocRef);
    if (existingSnap.exists() && !(existingSnap.data() as any)?.isDeleted) {
      throw new Error(`Device with ID '${trimmedId}' already exists.`);
    }

    await setDoc(devDocRef, {
      deviceId: trimmedId,
      deviceName: deviceData.deviceName.trim() || trimmedId,
      name: deviceData.deviceName.trim() || trimmedId,
      storeId: deviceData.storeId || '',
      storeDocId: deviceData.storeDocId || '',
      brandId: deviceData.brandId || '',
      useCash: deviceData.useCash !== false,
      useCard: deviceData.useCard !== false,
      isTesting: deviceData.isTesting === true,
      isDeleted: false,
      createdAt: new Date(),
      updatedAt: new Date()
    }, { merge: true });

    await this.syncDeviceCounts();
  }

  async updateDeviceDetails(deviceId: string, updates: {
    deviceName?: string;
    storeId?: string;
    storeDocId?: string;
    brandId?: string;
    useCash?: boolean;
    useCard?: boolean;
    isTesting?: boolean;
  }): Promise<void> {
    const devDocRef = doc(this.firestore, 'devices', deviceId);
    const updatePayload: any = {
      updatedAt: new Date()
    };

    if (updates.deviceName !== undefined) {
      updatePayload.deviceName = updates.deviceName.trim();
      updatePayload.name = updates.deviceName.trim();
    }
    if (updates.storeId !== undefined) updatePayload.storeId = updates.storeId;
    if (updates.storeDocId !== undefined) updatePayload.storeDocId = updates.storeDocId;
    if (updates.brandId !== undefined) updatePayload.brandId = updates.brandId;
    if (updates.useCash !== undefined) updatePayload.useCash = updates.useCash;
    if (updates.useCard !== undefined) updatePayload.useCard = updates.useCard;
    if (updates.isTesting !== undefined) updatePayload.isTesting = updates.isTesting;

    await updateDoc(devDocRef, updatePayload);
    await this.syncDeviceCounts();
  }
}

